/**
 * Scryfall-style query parser for the Pokemon Pokedex.
 *
 * Supported syntax:
 *   type:fire            → Fire-type Pokemon
 *   bst>=500             → BST at least 500
 *   gen:1                → Generation 1
 *   is:legendary         → Legendary Pokemon
 *   -type:normal         → NOT Normal type (negation)
 *   sort:speed           → Sort by Speed descending
 *   char                 → Bare word → name contains "char"
 */

// Canonical field names after alias resolution
const FIELD_ALIASES = {
  t: 'type',
  n: 'name',
  g: 'gen',
  a: 'ability',
  w: 'weight',
  o: 'sort',
  m: 'move',
  attack: 'atk',
  defense: 'def',
  spatk: 'spa',
  spdef: 'spd',
  speed: 'spe',
  r: 'resists',
  h: 'hits',
};

// Fields that accept numeric comparison operators
const NUMERIC_FIELDS = new Set(['bst', 'hp', 'atk', 'def', 'spa', 'spd', 'spe', 'weight', 'id', 'gen']);

// Valid values for `is:` keyword
export const IS_KEYWORDS = ['legendary', 'mythical', 'pseudo', 'baby', 'starter', 'mono', 'dual', 'mega', 'gmax', 'restricted'];

// Valid values for `in:` keyword
export const IN_KEYWORDS = ['champ'];

// Valid sort fields
export const SORT_FIELDS = ['id', 'name', 'bst', 'hp', 'atk', 'def', 'spa', 'spd', 'spe', 'weight'];

// Move name normalisation: 'Fake Out', 'fake-out', 'fake out' → canonical key for matching
export function normaliseMoveQuery(raw) {
  return raw.toLowerCase().replace(/[-\s]+/g, ' ').trim();
}

// All 18 types
export const ALL_TYPES = [
  'Normal', 'Fire', 'Water', 'Electric', 'Grass', 'Ice', 'Fighting', 'Poison',
  'Ground', 'Flying', 'Psychic', 'Bug', 'Rock', 'Ghost', 'Dragon', 'Dark', 'Steel', 'Fairy',
];

/**
 * Parse a raw query string into structured tokens.
 * @param {string} rawQuery
 * @returns {{ tokens: ParsedToken[], sortBy: string, sortDir: 'asc'|'desc', errors: string[] }}
 */
export function parseQuery(rawQuery) {
  if (!rawQuery || !rawQuery.trim()) {
    return { tokens: [], sortBy: 'id', sortDir: 'asc', errors: [] };
  }

  const tokens = [];
  let sortBy = 'id';
  let sortDir = 'asc';
  const errors = [];

  const parts = splitQuery(rawQuery);

  for (const part of parts) {
    if (!part) continue;
    const token = parsePart(part, errors);
    if (!token) continue;

    if (token.field === 'sort') {
      [sortBy, sortDir] = parseSort(token.value);
    } else if (token.field === 'sorthigh') {
      [sortBy] = parseSort(token.value);
      sortDir = 'desc';
    } else if (token.field === 'sortlow') {
      [sortBy] = parseSort(token.value);
      sortDir = 'asc';
    } else {
      tokens.push(token);
    }
  }

  return { tokens, sortBy, sortDir, errors };
}

/**
 * Split query on whitespace, respecting double-quoted strings and (…) groups.
 * A parenthesised group is returned as a single part including the parens.
 */
function splitQuery(query) {
  const parts = [];
  let current = '';
  let inQuote = false;
  let depth = 0;

  for (const ch of query) {
    if (ch === '"') {
      inQuote = !inQuote;
      current += ch;
    } else if (!inQuote && ch === '(') {
      depth++;
      current += ch;
    } else if (!inQuote && ch === ')') {
      depth--;
      current += ch;
      if (depth === 0) {
        parts.push(current);
        current = '';
      }
    } else if (ch === ' ' && !inQuote && depth === 0) {
      if (current) parts.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  if (current) parts.push(current);
  return parts;
}

/**
 * Parse a single whitespace-separated token.
 * Returns a ParsedToken or null if unparseable.
 */
function parsePart(part, errors) {
  // OR group: (token1 or token2 or ...)
  // Optionally prefixed with - or ! for negation of the whole group
  const negateGroup = part.startsWith('-') || part.startsWith('!');
  const groupCandidate = negateGroup ? part.slice(1) : part;
  if (groupCandidate.startsWith('(') && groupCandidate.endsWith(')')) {
    const inner = groupCandidate.slice(1, -1);
    // Split on ' or ' (case-insensitive)
    const subParts = inner.split(/\s+or\s+/i);
    const subTokens = subParts
      .map(sp => parsePart(sp.trim(), errors))
      .filter(Boolean);
    if (subTokens.length === 0) return null;
    return { field: 'or-group', tokens: subTokens, negate: negateGroup, raw: part };
  }

  // Negation prefix
  const negate = part.startsWith('-') || part.startsWith('!');
  const stripped = negate ? part.slice(1) : part;

  // Structured token: field op value   e.g. "type:fire", "bst>=500", "atk>100"
  const match = stripped.match(/^([a-z]+)(>=|<=|>|<|:)(.+)$/i);
  if (match) {
    let [, field, op, value] = match;
    field = field.toLowerCase();
    field = FIELD_ALIASES[field] ?? field;
    // Strip surrounding quotes (e.g. move:"fake out" → fake out)
    if (value.startsWith('"') && value.endsWith('"')) value = value.slice(1, -1);
    value = value.toLowerCase();

    // Validate numeric fields get numeric values
    if (NUMERIC_FIELDS.has(field) && op !== ':' && isNaN(Number(value))) {
      errors.push(`"${field}${op}${value}" — expected a number`);
      return null;
    }

    return { field, op, value, negate, raw: part };
  }

  // Bare word → name partial match  (letters, digits, hyphens)
  if (/^[\w-]+$/.test(stripped)) {
    return { field: 'name', op: ':', value: stripped.toLowerCase(), negate, raw: part };
  }

  errors.push(`Unrecognised token: "${part}"`);
  return null;
}

/**
 * Parse sort value like "speed", "bst-desc", "name-asc".
 * Default direction: asc for id/name, desc for stats.
 */
function parseSort(value) {
  const dirMatch = value.match(/^(.+)-(asc|desc)$/);
  if (dirMatch) return [dirMatch[1], dirMatch[2]];

  const field = value;
  const dir = field === 'id' || field === 'name' ? 'asc' : 'desc';
  return [field, dir];
}

/**
 * Given the word currently being typed, return autocomplete suggestions.
 * Returns null if no suggestions apply.
 *
 * @param {string} currentWord   — the partial token at cursor, e.g. "type:fi"
 * @param {string[]} allAbilities — list of all ability names for ability autocomplete
 * @returns {{ prefix: string, completions: string[] } | null}
 */
export function getAutocompleteSuggestions(currentWord, allAbilities = []) {
  const word = currentWord.toLowerCase();

  // type: / t:
  for (const pfx of ['type:', 't:']) {
    if (word.startsWith(pfx)) {
      const typed = word.slice(pfx.length);
      const matches = ALL_TYPES.filter(t => t.toLowerCase().startsWith(typed));
      return matches.length ? { prefix: pfx, completions: matches.map(t => t.toLowerCase()) } : null;
    }
  }

  // gen: / g:
  for (const pfx of ['gen:', 'g:']) {
    if (word.startsWith(pfx)) {
      const typed = word.slice(pfx.length);
      const gens = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];
      const matches = gens.filter(g => g.startsWith(typed));
      return matches.length ? { prefix: pfx, completions: matches } : null;
    }
  }

  // is:
  if (word.startsWith('is:')) {
    const typed = word.slice(3);
    const matches = IS_KEYWORDS.filter(k => k.startsWith(typed));
    return matches.length ? { prefix: 'is:', completions: matches } : null;
  }

  // in:
  if (word.startsWith('in:')) {
    const typed = word.slice(3);
    const matches = IN_KEYWORDS.filter(k => k.startsWith(typed));
    return matches.length ? { prefix: 'in:', completions: matches } : null;
  }

  // sort: / o:
  for (const pfx of ['sort:', 'o:']) {
    if (word.startsWith(pfx)) {
      const typed = word.slice(pfx.length);
      const matches = SORT_FIELDS.filter(f => f.startsWith(typed));
      return matches.length ? { prefix: pfx, completions: matches } : null;
    }
  }

  // resists: / r:
  for (const pfx of ['resists:', 'r:']) {
    if (word.startsWith(pfx)) {
      const typed = word.slice(pfx.length);
      const matches = ALL_TYPES.filter(t => t.toLowerCase().startsWith(typed));
      return matches.length ? { prefix: pfx, completions: matches.map(t => t.toLowerCase()) } : null;
    }
  }

  // hits: / h:
  for (const pfx of ['hits:', 'h:']) {
    if (word.startsWith(pfx)) {
      const typed = word.slice(pfx.length);
      const matches = ALL_TYPES.filter(t => t.toLowerCase().startsWith(typed));
      return matches.length ? { prefix: pfx, completions: matches.map(t => t.toLowerCase()) } : null;
    }
  }

  // ability: / a:  (fuzzy — min 2 chars)
  for (const pfx of ['ability:', 'a:']) {
    if (word.startsWith(pfx)) {
      const typed = word.slice(pfx.length);
      if (typed.length >= 2) {
        const matches = allAbilities
          .filter(a => a.toLowerCase().includes(typed))
          .slice(0, 8);
        return matches.length ? { prefix: pfx, completions: matches.map(a => a.toLowerCase()) } : null;
      }
      return null;
    }
  }

  // Empty prefix hints — show prefix options when user has typed a known prefix alone
  const PREFIX_HINTS = {
    'type:': ALL_TYPES.map(t => t.toLowerCase()),
    't:': ALL_TYPES.map(t => t.toLowerCase()),
    'gen:': ['1', '2', '3', '4', '5', '6', '7', '8', '9'],
    'g:': ['1', '2', '3', '4', '5', '6', '7', '8', '9'],
    'is:': IS_KEYWORDS,
    'in:': IN_KEYWORDS,
    'sort:': SORT_FIELDS,
    'o:': SORT_FIELDS,
    'sortlow:': SORT_FIELDS,
    'sorthigh:': SORT_FIELDS,
    'resists:': ALL_TYPES.map(t => t.toLowerCase()),
    'r:': ALL_TYPES.map(t => t.toLowerCase()),
    'hits:': ALL_TYPES.map(t => t.toLowerCase()),
    'h:': ALL_TYPES.map(t => t.toLowerCase()),
  };

  if (PREFIX_HINTS[word]) {
    return { prefix: word, completions: PREFIX_HINTS[word] };
  }

  return null;
}

/** Extract the word currently being typed at a given cursor position. */
export function getCurrentWord(value, cursorPos) {
  const before = value.slice(0, cursorPos);
  const lastSpace = before.lastIndexOf(' ');
  return before.slice(lastSpace + 1);
}

/** Remove a token's raw text from the query string. */
export function removeTokenFromQuery(query, rawToken) {
  // Escape special regex chars in the raw token
  const escaped = rawToken.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return query.replace(new RegExp(`\\s*${escaped}\\s*`), ' ').trim();
}
